// widget/src/embed.tsx
// This is the entry point that builds into a single JS file for embedding

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ChatWidget } from './components/ChatWidget';

interface WidgetConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
}

// Global initialization function
function initDrTJWidget(config: WidgetConfig) {
  // Validate required config
  if (!config.supabaseUrl || !config.supabaseAnonKey) {
    console.error('DrTJWidget: supabaseUrl and supabaseAnonKey are required');
    return;
  }

  // Create container
  const containerId = 'drtj-chat-widget-container';
  let container = document.getElementById(containerId);
  
  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    document.body.appendChild(container);
  }

  // Render widget
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <ChatWidget
        supabaseUrl={config.supabaseUrl}
        supabaseAnonKey={config.supabaseAnonKey}
        position={config.position || 'bottom-right'}
        primaryColor={config.primaryColor || '#d4a266'}
      />
    </React.StrictMode>
  );

  // Return cleanup function
  return () => {
    root.unmount();
    container?.remove();
  };
}

// Expose to global scope
declare global {
  interface Window {
    DrTJWidget: {
      init: typeof initDrTJWidget;
    };
  }
}

window.DrTJWidget = {
  init: initDrTJWidget,
};

// Auto-init if config is present on page load
document.addEventListener('DOMContentLoaded', () => {
  const configScript = document.querySelector('script[data-drtj-config]');
  if (configScript) {
    try {
      const config = JSON.parse(configScript.getAttribute('data-drtj-config') || '{}');
      initDrTJWidget(config);
    } catch (e) {
      console.error('DrTJWidget: Failed to parse config', e);
    }
  }
});

export { initDrTJWidget };
